import { Button } from "@admin/components/atoms/Button";
import { ErrorMessage } from "@admin/components/atoms/ErrorMessage";
import { Input } from "@admin/components/atoms/Input";
import { Label } from "@admin/components/atoms/Input/Label";
import { Modal, ModalProps } from "@admin/components/atoms/Modal";
import { addOpenShift, dateToISO } from "@enos-monorepo/lib";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation,useQueryClient } from "@tanstack/react-query";
import { addDays, addHours } from "date-fns";
import { useCallback,useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { z } from "zod";

interface NewOpenShiftDailyModalProps extends ModalProps {
  companyId: string;
  companyLocationId: string;
  selectedDate: Date;
}

export function NewOpenShiftDailyModal({
  selectedDate,
  companyId,
  companyLocationId,
  onClose,
  ...props
}: NewOpenShiftDailyModalProps) {
  const { t, i18n } = useTranslation();
  // const teamOptions: Option[] = [
  //   { value: 1, label: "Team 1" },
  //   { value: 2, label: "Team 2" },
  //   { value: 3, label: "Team 3" },
  //   { value: 4, label: "Team 4" },
  //   { value: 5, label: "Team 5" },
  // ];

  const [shiftNumber, setShiftNumber] = useState(1);
  const [calculatedTime, setCalculatedTime] = useState(0);
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");

  const processHours = () => {
    const isNextDay = getHoursInNumber(endTime) < getHoursInNumber(startTime);
    return [
      dateToISO(addHours(selectedDate, getHoursInNumber(startTime))),
      !isNextDay
        ? dateToISO(addHours(selectedDate, getHoursInNumber(endTime)))
        : dateToISO(
            addHours(addDays(selectedDate, 1), getHoursInNumber(endTime)),
          ),
    ];
  };
  const newDailyOpenShiftSchema = z.object({
    startTime: z.string().min(1, t("shift.Start time is required")),
    endTime: z.string().min(1, t("shift.End time is required")),
  });
  type NewDailyOpenShiftData = z.infer<typeof newDailyOpenShiftSchema>;

  const onSubmit = () => {
    const [start, end] = processHours();
    const payload = {
      companyId: companyId,
      companyLocationId: companyLocationId,
      plannedStartTime: start,
      plannedEndTime: end,
    };
    const finalPayload = Array(shiftNumber).fill(payload);

    mutation.mutate(finalPayload);
  };

  const queryClient = useQueryClient();
  const {
    handleSubmit,
    register,
    formState: { errors },
    reset,
  } = useForm<NewDailyOpenShiftData>({
    resolver: zodResolver(newDailyOpenShiftSchema),
  });

  const mutation = useMutation({
    mutationFn: addOpenShift,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["shifts", companyLocationId],
      });
      setStartTime("");
      setEndTime("");
      reset();
      onClose?.();
      toast.success(t("shift.Shifts have been planned"), {
        duration: 4000,
      });
    },
    onError: (err: Error) => {
      toast.error(t("shift.Unable to plan shifts"));
    },
  });

  const getHoursInNumber = (time: string): number => {
    if (!time) return 0;
    const [hour, minute] = time.split(":").map(Number);
    return hour + minute / 60;
  };

  const calculateTimeDifference = (start: string, end: string): number => {
    if (!start || !end) return 0.0;

    const [startHour, startMinute] = start.split(":").map(Number);
    const [endHour, endMinute] = end.split(":").map(Number);

    const startDate = new Date();
    startDate.setHours(startHour, startMinute, 0);

    const endDate = new Date();
    endDate.setHours(endHour, endMinute, 0);

    if (endDate < startDate) {
      endDate.setDate(endDate.getDate() + 1);
    }
    const diffInMs = endDate.getTime() - startDate.getTime();
    const diffInHours = diffInMs / (1000 * 60 * 60);
    return parseFloat(diffInHours.toFixed(2));
  };

  useEffect(() => {
    const timeDiff = calculateTimeDifference(startTime, endTime);
    setCalculatedTime(timeDiff);
  }, [startTime, endTime]);

  const handleClose = useCallback(() => {
    onClose?.();
    setStartTime("");
    setEndTime("");
    setCalculatedTime(0);
  }, [onClose]);

  return (
    <Modal
      onClose={handleClose}
      actionButtons={
        <>
          <Button variant="secondary" onClick={handleClose}>
            {t("common.Discard")}
          </Button>
          <Button loading={mutation.isPending} onClick={handleSubmit(onSubmit)}>
            {t("shift.Plan Shifts")}
          </Button>
        </>
      }
      {...props}
      title={t("shift.Plan New Shifts to", {
        date: selectedDate
          ? Intl.DateTimeFormat(i18n.language, {
              day: "2-digit", // 'dd' karşılığı
              month: "short", // 'MMM' karşılığı
            }).format(selectedDate)
          : "",
      })}
    >
      <form>
        {/* <Label title={t("shift.Team Restrictions")}>
          <Select
            options={teamOptions}
            placeholder="Soon..."
            size="md"
            disabled
          />
        </Label> */}

        <Label title={t("shift.Shift Beginning and End")}>
          <div className="flex flex-row justify-between w-full gap-2">
            <div className="flex flex-col w-full gap-y-1">
              <Input
                {...register("startTime")}
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                type="time"
              />
              <ErrorMessage message={errors?.startTime?.message} />
            </div>
            <div className="flex flex-col w-full gap-y-1">
              <Input
                {...register("endTime")}
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
                type="time"
              />
              <ErrorMessage message={errors?.endTime?.message} />
            </div>
          </div>
        </Label>

        <Label title={t("shift.Number of Open Shifts")}>
          <Input
            type="number"
            value={shiftNumber}
            onChange={(e) => {
              setShiftNumber(parseInt(e.target.value));
              if (parseInt(e.target.value) < 1) {
                setShiftNumber(1);
              }
            }}
          />
        </Label>
        <Label title={t("shift.Calculated Time")}>
          <Input value={calculatedTime} disabled />
        </Label>
      </form>
    </Modal>
  );
}
